/**
 * Maintenance mode on screen: the splash page, the banner, and the popup.
 *
 * SigmaBuddy's SIGM-205 `MaintenancePage`, `MaintenanceBanner` and
 * `MaintenanceAnnouncementModal`, adapted. Dumb renderers: every decision is a
 * pure function in `@truckbuddy/contract` (`site-maintenance.ts`), and the shell
 * decides which of these to show.
 *
 * The page is rendered IN PLACE at whatever address the person is on, never at a
 * route of its own. A route would mean a history entry, a refresh that has to be
 * handled, and a navigation back when the window ends. This way the poll flips
 * the state and the screen they were on simply renders underneath them again,
 * with nothing to click.
 */
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import {
  formatSiteMaintenanceCountdown,
  formatSiteMaintenanceTime,
  msUntilSiteMaintenance,
  shouldShowSiteMaintenanceAnnouncement,
  siteMaintenanceAnnouncementSlot,
  type SiteMaintenanceStatus,
} from "@truckbuddy/contract";
import {
  SITE_MAINTENANCE_ACK_FINAL_KEY,
  SITE_MAINTENANCE_ACK_KEY,
  readSiteMaintenanceAck,
  useNow,
  writeSiteMaintenanceAck,
} from "../lib/site-maintenance-queries";
import { SignIn } from "../screens/SignIn";
import { BrandMark } from "./BrandMark";
import { Dialog } from "./Dialog";

/** The line the page carries when the admin who armed the window wrote none. */
export const SITE_MAINTENANCE_DEFAULT_BODY =
  "We're making some changes. Nothing you had open has been lost, and this page " +
  "will let you back in as soon as we're done.";

/**
 * What everyone who cannot work through an active window sees.
 *
 * It carries an admin sign-in, because `/auth/*` stays open during a window so an
 * administrator can get in and check the release that caused it. Somebody signed
 * in without that right is told so, rather than offered a form that would only
 * bring them back here.
 */
export function SiteMaintenancePage({
  status,
  signedIn,
  checking,
  onCheckAgain,
  onSignedIn,
}: {
  status: SiteMaintenanceStatus | null;
  /** Signed in, and turned away anyway: the account cannot work through a window. */
  signedIn: boolean;
  checking: boolean;
  onCheckAgain: () => void;
  onSignedIn: () => void;
}) {
  const [signingIn, setSigningIn] = useState(false);
  const titleId = useId();

  if (signingIn) {
    return (
      <>
        <div className="site-maintenance-back">
          <button type="button" className="btn btn-ghost" onClick={() => setSigningIn(false)}>
            ← Back to the maintenance notice
          </button>
        </div>
        <SignIn onSignedIn={onSignedIn} />
      </>
    );
  }

  const zone = status?.fleetTimeZone ?? "America/Chicago";
  const backBy = formatSiteMaintenanceTime(status?.expectedEndAt ?? null, zone);

  return (
    <main className="screen-body site-maintenance-page" aria-labelledby={titleId}>
      <div className="panel stack">
        <div className="nav-brand">
          <BrandMark />
        </div>
        <h1 className="h-3 site-maintenance-title" id={titleId}>
          Down for maintenance
        </h1>
        <p className="site-maintenance-body">{status?.message || SITE_MAINTENANCE_DEFAULT_BODY}</p>
        {backBy ? (
          <p className="site-maintenance-eta">
            Expected back by <strong>{backBy}</strong>
          </p>
        ) : null}
        {signedIn ? (
          <p className="text-muted site-maintenance-note">
            You&rsquo;re signed in. TruckBuddy opens for you by itself when maintenance ends.
          </p>
        ) : null}
        <div className="row site-maintenance-actions">
          {/* The page re-checks on its own; this is for the person who would
              otherwise sit pressing F5, and gives them something that works. */}
          <button type="button" className="btn btn-primary" onClick={onCheckAgain} disabled={checking}>
            {checking ? "Checking…" : "Check again"}
          </button>
          {signedIn ? null : (
            <button type="button" className="btn btn-secondary" onClick={() => setSigningIn(true)}>
              Admin sign-in
            </button>
          )}
        </div>
        <p className="text-muted site-maintenance-foot">
          This page checks by itself, so there&rsquo;s no need to refresh.
        </p>
      </div>
    </main>
  );
}

/**
 * The strip above the nav: a countdown while a window is scheduled, and, for an
 * admin working through an active one, a standing reminder that nobody else can.
 *
 * Not dismissible, either of them. The countdown is the whole point of the
 * scheduled state, and an admin smoke-testing a release that looks fine has no
 * other way to know the rest of the company is looking at a maintenance page.
 */
export function SiteMaintenanceBanner({
  status,
  receivedAt,
  canManage,
}: {
  status: SiteMaintenanceStatus | null | undefined;
  /** When the status arrived, for the countdown's clock correction. */
  receivedAt: number;
  canManage: boolean;
}) {
  const scheduled = status?.state === "scheduled";
  const now = useNow(1_000, scheduled);
  if (!status || status.state === "off") return null;

  const manage = canManage ? (
    <Link to="/settings/maintenance">Maintenance mode</Link>
  ) : null;

  if (status.state === "active") {
    return (
      <div className="site-maintenance-banner site-maintenance-banner--bypass" role="status">
        <span aria-hidden="true">■</span>
        <span>
          <strong>Maintenance is on.</strong> Everyone else sees the maintenance page. You can keep
          working because you&rsquo;re an admin.
        </span>
        {manage}
      </div>
    );
  }

  const until = msUntilSiteMaintenance(status, now, receivedAt);
  const at = formatSiteMaintenanceTime(status.startsAt, status.fleetTimeZone);
  return (
    <div className="site-maintenance-banner site-maintenance-banner--scheduled" role="status">
      <span aria-hidden="true">◷</span>
      <span>
        <strong>
          Maintenance {until !== null && until > 0 ? `in ${formatSiteMaintenanceCountdown(until)}` : "starting now"}
        </strong>
        {at ? ` · ${at}` : ""}
        {status.message ? ` · ${status.message}` : ""}
      </span>
      {manage}
    </div>
  );
}

/**
 * The popup, at most twice per announcement: once when first seen, once five
 * minutes out. Any more and people learn to close it unread.
 */
export function SiteMaintenanceAnnouncement({
  status,
  receivedAt,
}: {
  status: SiteMaintenanceStatus | null | undefined;
  receivedAt: number;
}) {
  const scheduled = status?.state === "scheduled";
  // A coarse tick is enough: it only has to notice the five-minute line.
  const now = useNow(15_000, scheduled);
  const [acked, setAcked] = useState(() => readSiteMaintenanceAck(SITE_MAINTENANCE_ACK_KEY));
  const [ackedFinal, setAckedFinal] = useState(() =>
    readSiteMaintenanceAck(SITE_MAINTENANCE_ACK_FINAL_KEY),
  );

  if (!status || !scheduled) return null;
  const until = msUntilSiteMaintenance(status, now, receivedAt);
  if (!shouldShowSiteMaintenanceAnnouncement(status, until, acked, ackedFinal)) return null;

  const close = () => {
    // Acknowledging the final warning also covers the first: somebody who first
    // sees the window five minutes out has read everything there is to read.
    writeSiteMaintenanceAck(SITE_MAINTENANCE_ACK_KEY, status.announcementId);
    setAcked(status.announcementId);
    if (siteMaintenanceAnnouncementSlot(until) === "final") {
      writeSiteMaintenanceAck(SITE_MAINTENANCE_ACK_FINAL_KEY, status.announcementId);
      setAckedFinal(status.announcementId);
    }
  };

  const at = formatSiteMaintenanceTime(status.startsAt, status.fleetTimeZone);
  const backBy = formatSiteMaintenanceTime(status.expectedEndAt, status.fleetTimeZone);
  const final = siteMaintenanceAnnouncementSlot(until) === "final";

  return (
    <Dialog
      title={final ? "Maintenance starts in a few minutes" : "Maintenance scheduled"}
      onClose={close}
      actions={
        <button type="button" className="btn btn-primary" onClick={close}>
          Got it
        </button>
      }
    >
      <div className="stack">
        <p>
          TruckBuddy goes down for maintenance at <strong>{at}</strong>
          {until !== null && until > 0 ? ` (in ${formatSiteMaintenanceCountdown(until)})` : ""}.
          {backBy ? ` It should be back by ${backBy}.` : ""}
        </p>
        {status.message ? <p>{status.message}</p> : null}
        <p className="text-muted">Save what you&rsquo;re working on before then.</p>
      </div>
    </Dialog>
  );
}
